import { createSlice } from '@reduxjs/toolkit';

import axios from '../../utils/axios';
import { trackEvent, ANALYTICS_EVENTS } from '../../utils/posthog';

// ----------------------------------------------------------------------

const initialState = {
  subscription: null,
  loading: false,
  error: null,
};

const slice = createSlice({
  name: 'subscription',
  initialState,
  reducers: {
    // START LOADING
    startLoading (state) {
      state.loading = true;
      state.error = null;
    },

    // HAS ERROR
    hasError (state, action) {
      state.loading = false;
      state.error = action.payload;
    },

    // GET SUBSCRIPTION SUCCESS
    getSubscriptionSuccess (state, action) {
      state.loading = false;
      state.subscription = action.payload;
      state.error = null;
    },

    // CLEAR SUBSCRIPTION
    clearSubscription (state) {
      state.subscription = null;
      state.loading = false;
      state.error = null;
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
export const { clearSubscription } = slice.actions;

// ----------------------------------------------------------------------

/**
 * Get user's subscription details
 */
export function getSubscription () {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get('/api/payment/subscription');
      dispatch(slice.actions.getSubscriptionSuccess(response.data));
    } catch (error) {
      const errorMessage = error.response?.data?.message
        || "Couldn't load your plan. Refresh the page to try again.";
      dispatch(slice.actions.hasError(errorMessage));
      throw error;
    }
  };
}

/**
 * Create a checkout session and redirect to Stripe
 */
export async function createCheckoutSession (planId, billingCycle) {
  trackEvent(ANALYTICS_EVENTS.BILLING_CHECKOUT_STARTED, { plan: planId, billingCycle });
  try {
    // Capture Endorsely affiliate referral tracking ID from window object
    const endorselyReferral = window.endorsely_referral || null;

    const response = await axios.post('/api/payment/create-checkout-session', {
      planId,
      billingCycle,
      endorselyReferral,
    });
    return response.data; // { sessionId, url }
  } catch (error) {
    console.error('Failed to create checkout session:', error);
    trackEvent(ANALYTICS_EVENTS.BILLING_CHECKOUT_FAILED, { plan: planId, billingCycle });
    throw error;
  }
}

/**
 * Create a checkout session for a one-time add-on credit pack purchase
 */
export async function createAddonCheckoutSession (packId) {
  trackEvent(ANALYTICS_EVENTS.BILLING_ADDON_CHECKOUT_STARTED, { pack: packId });
  try {
    const response = await axios.post('/api/payment/create-addon-checkout', { packId });
    return response.data; // { sessionId, url }
  } catch (error) {
    console.error('Failed to create add-on checkout session:', error);
    throw error;
  }
}

/**
 * Create a customer portal session and redirect to Stripe
 */
export async function createPortalSession () {
  trackEvent(ANALYTICS_EVENTS.BILLING_PORTAL_OPENED);
  try {
    const response = await axios.post('/api/payment/create-portal-session');
    return response.data; // { url }
  } catch (error) {
    console.error('Failed to create portal session:', error);
    throw error;
  }
}

/**
 * Cancel the subscription at period end, with an optional reason.
 * payload: { reason, reasonDetail, comment } — reason defaults to 'skipped' server-side.
 */
export async function cancelSubscription (payload = {}) {
  // The chip is a stable identifier so it is safe for analytics; the free-text
  // comment is deliberately NOT sent here — it lives only in our DB and Stripe.
  trackEvent(ANALYTICS_EVENTS.BILLING_CANCEL_SUBMITTED, {
    reason: payload.reason || 'skipped',
    reasonDetail: payload.reasonDetail || null,
    hasComment: Boolean(payload.comment),
  });
  try {
    const response = await axios.post('/api/payment/cancel-subscription', payload);
    return response.data; // { cancelAtPeriodEnd, currentPeriodEnd }
  } catch (error) {
    console.error('Failed to cancel subscription:', error);
    throw error;
  }
}

/**
 * "Keep my plan" — undo a pending cancellation.
 */
export async function resumeSubscription () {
  trackEvent(ANALYTICS_EVENTS.BILLING_CANCEL_KEPT_PLAN);
  try {
    const response = await axios.post('/api/payment/resume-subscription');
    return response.data; // { cancelAtPeriodEnd, currentPeriodEnd }
  } catch (error) {
    console.error('Failed to resume subscription:', error);
    throw error;
  }
}
